import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { FileText, Upload, Check, AlertCircle, Loader2, X, CheckCircle2, Leaf } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClientDocuments = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [uploadedDocs, setUploadedDocs] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/client-documents/${token}`);
      setData(res.data);
      if (res.data.documents_submitted) {
        setSubmitted(true);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Ce lien est invalide ou a expiré. Veuillez contacter votre gestionnaire pour obtenir un nouveau lien.');
      } else {
        setError('Une erreur est survenue. Veuillez réessayer.');
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleFileUpload = async (doc, file) => {
    if (!file) return;

    setUploadingDoc(doc.code);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type_code', doc.code);
    formData.append('document_type_nom', doc.nom);

    try {
      await axios.post(`${API_URL}/api/client-documents/${token}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setUploadedDocs(prev => ({ ...prev, [doc.code]: file.name }));
      // Recharger pour mettre à jour la liste
      await loadData();
    } catch (err) {
      const message = err.response?.data?.detail || 'Erreur lors de l\'upload';
      alert(message);
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/client-documents/${token}/submit`);
      setSubmitted(true);
    } catch (err) {
      alert('Erreur lors de la soumission');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-red-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Lien invalide</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-green-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Merci !</h1>
          <p className="text-gray-600 mb-4">
            Vos documents ont été envoyés avec succès.
          </p>
          <p className="text-sm text-gray-500">
            Votre gestionnaire va les examiner et vous recontactera si nécessaire.
          </p>
          {data?.reference && (
            <p className="mt-6 text-xs text-gray-400">
              Référence dossier : {data.reference}
            </p>
          )}
        </div>
      </div>
    );
  }

  const hasUploadedAny = Object.keys(uploadedDocs).length > 0 || 
    (data?.missing_documents && data.missing_documents.length < data.total_missing);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="text-center mb-4">
            <h1 className="text-4xl font-bold text-gray-900 flex items-center justify-center mb-2">
              Soc<span className="relative inline-block">
                <span className="invisible">i</span>
                <span className="absolute bottom-0 left-0">ı</span>
                <Leaf className="absolute -top-2 left-0 w-5 h-5 text-emerald-500 fill-emerald-500" />
              </span>alAC
            </h1>
            <p className="text-gray-600">Documents à fournir</p>
            <p className="text-gray-600">
              {data?.client_nom && `Bonjour ${data.client_nom}`}
            </p>
          </div>
          
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
            <p className="text-blue-800 text-sm">
              📋 Veuillez téléverser les documents manquants ci-dessous pour compléter votre dossier.
              Les formats acceptés sont : <strong>PDF, JPEG, PNG, WEBP, DOC, DOCX</strong> (max 10 MB par fichier).
            </p>
          </div>
        </div>

        {/* Liste des documents manquants */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span className="text-xl">📄</span>
            Documents manquants ({data?.missing_documents?.length || 0})
          </h2>

          {data?.missing_documents?.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-green-500" />
              <p>Tous les documents ont été reçus !</p>
            </div>
          ) : (
            <div className="space-y-3">
              {data?.missing_documents?.map((doc) => (
                <div 
                  key={doc.id}
                  className={`p-4 rounded-xl border-2 transition ${
                    uploadedDocs[doc.code] 
                      ? 'border-green-300 bg-green-50' 
                      : doc.obligatoire 
                        ? 'border-red-200 bg-red-50' 
                        : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{doc.nom}</h3>
                        {doc.obligatoire && (
                          <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                            Obligatoire
                          </span>
                        )}
                      </div>
                      {uploadedDocs[doc.code] && (
                        <p className="text-sm text-green-600 mt-1 flex items-center gap-1">
                          <Check className="w-4 h-4" />
                          {uploadedDocs[doc.code]}
                        </p>
                      )}
                    </div>
                    
                    <div className="flex-shrink-0">
                      {uploadedDocs[doc.code] ? (
                        <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                          <Check className="w-5 h-5 text-white" />
                        </div>
                      ) : uploadingDoc === doc.code ? (
                        <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                          <Loader2 className="w-5 h-5 text-white animate-spin" />
                        </div>
                      ) : (
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            className="hidden"
                            accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
                            onChange={(e) => handleFileUpload(doc, e.target.files[0])}
                          />
                          <div className="w-10 h-10 bg-blue-600 hover:bg-blue-700 rounded-lg flex items-center justify-center transition">
                            <Upload className="w-5 h-5 text-white" />
                          </div>
                        </label>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bouton de soumission */}
        {hasUploadedAny && (
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full py-4 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold text-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Check className="w-5 h-5" />
                  J'ai terminé, envoyer mes documents
                </>
              )}
            </button>
            <p className="text-center text-sm text-gray-500 mt-3">
              Vous pouvez envoyer vos documents même si certains sont manquants.
              Vous pourrez les compléter plus tard avec un nouveau lien.
            </p>
          </div>
        )}

        {/* Footer */}
        <p className="text-center text-xs text-gray-400 mt-6">
          {data?.reference && `Dossier ${data.reference}`}
        </p>
      </div>
    </div>
  );
};

export default ClientDocuments;
